import { supabase } from './supabase';

export type AssessmentType = 'pilot' | 'instructor' | 'debriefing_guide';

export interface AssessmentVersion {
  id: string;
  assessment_id: string;
  assessment_type: AssessmentType;
  version_number: number;
  snapshot: Record<string, unknown>;
  is_ai_baseline: boolean;
  created_by: string | null;
  created_at: string;
  change_summary: string | null;
}

export interface CreateVersionOptions {
  isAiBaseline?: boolean;
  changeSummary?: string;
}

/**
 * Inserts a new immutable snapshot into assessment_versions.
 * version_number is calculated as MAX(version_number) + 1 for the assessment,
 * or 1 if no versions exist yet.
 *
 * Returns null on failure — a failed version write must not block the caller's save flow.
 */
export async function createVersion(
  assessmentId: string,
  assessmentType: AssessmentType,
  payload: Record<string, unknown>,
  userId: string | null,
  options: CreateVersionOptions = {}
): Promise<AssessmentVersion | null> {
  try {
    const { data, error } = await supabase
      .rpc('create_assessment_version', {
        p_assessment_id: assessmentId,
        p_assessment_type: assessmentType,
        p_snapshot: payload,
        p_created_by: userId,
        p_is_ai_baseline: options.isAiBaseline ?? false,
        p_change_summary: options.changeSummary ?? null,
      })
      .select()
      .single();

    if (error) {
      console.error('[assessment-versions] Failed to create version via RPC:', error);
      return null;
    }

    return data as AssessmentVersion;
  } catch (err) {
    console.error('[assessment-versions] Unexpected error in createVersion:', err);
    return null;
  }
}

/**
 * Returns all versions for an assessment ordered by version_number DESC.
 */
export async function getVersions(
  assessmentId: string,
  assessmentType: AssessmentType
): Promise<AssessmentVersion[]> {
  const { data, error } = await supabase
    .from('assessment_versions')
    .select('*')
    .eq('assessment_id', assessmentId)
    .eq('assessment_type', assessmentType)
    .order('version_number', { ascending: false });

  if (error) throw error;
  return (data ?? []) as AssessmentVersion[];
}

/**
 * Returns a specific version by version_number, or null if not found.
 */
export async function getVersion(
  assessmentId: string,
  assessmentType: AssessmentType,
  versionNumber: number
): Promise<AssessmentVersion | null> {
  const { data, error } = await supabase
    .from('assessment_versions')
    .select('*')
    .eq('assessment_id', assessmentId)
    .eq('assessment_type', assessmentType)
    .eq('version_number', versionNumber)
    .maybeSingle();

  if (error) throw error;
  return data as AssessmentVersion | null;
}

/**
 * Returns the AI-generated baseline version (is_ai_baseline = true),
 * or null if not found.
 */
export async function getAiBaseline(
  assessmentId: string,
  assessmentType: AssessmentType
): Promise<AssessmentVersion | null> {
  const { data, error } = await supabase
    .from('assessment_versions')
    .select('*')
    .eq('assessment_id', assessmentId)
    .eq('assessment_type', assessmentType)
    .eq('is_ai_baseline', true)
    .maybeSingle();

  if (error) throw error;
  return data as AssessmentVersion | null;
}

/**
 * Returns the most recent version (highest version_number), or null if none exist.
 */
export async function getLatestVersion(
  assessmentId: string,
  assessmentType: AssessmentType
): Promise<AssessmentVersion | null> {
  const { data, error } = await supabase
    .from('assessment_versions')
    .select('*')
    .eq('assessment_id', assessmentId)
    .eq('assessment_type', assessmentType)
    .order('version_number', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return data as AssessmentVersion | null;
}
